import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

const KYB_STATUSES = ["", "draft", "submitted", "activated", "rejected", "additional_info_requested"];
const BUSINESS_TYPES = ["", "sole_proprietor", "partnership", "company", "limited_liability_partnership"];

export default function AdminUserList() {
  const [q, setQ] = useState("");
  const [kybStatus, setKybStatus] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Debounced search
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/users", {
          params: {
            q: q || undefined,
            kyb_status: kybStatus || undefined,
            business_type: businessType || undefined,
            limit: 200,
          },
        });
        setRows(data.users);
        setTotal(data.total);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, kybStatus, businessType]);

  return (
    <div className="space-y-4" data-testid="admin-user-list">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Users</h1>
          <p className="text-sm text-slate-500 mt-1">{total} matching · showing latest 200</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email or legal name…"
              className="pl-9 h-10"
              data-testid="admin-user-search"
            />
          </div>
          <SelectFilter
            value={kybStatus}
            onChange={setKybStatus}
            options={KYB_STATUSES}
            label="All KYB statuses"
            testid="admin-filter-kyb"
          />
          <SelectFilter
            value={businessType}
            onChange={setBusinessType}
            options={BUSINESS_TYPES}
            label="All business types"
            testid="admin-filter-bt"
          />
          {(q || kybStatus || businessType) && (
            <button
              onClick={() => {
                setQ("");
                setKybStatus("");
                setBusinessType("");
              }}
              className="text-xs text-slate-500 hover:text-slate-800"
              data-testid="admin-filter-clear"
            >
              Clear
            </button>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <Th>Business</Th>
                <Th>Email</Th>
                <Th>Type</Th>
                <Th>KYB status</Th>
                <Th>Onboarded</Th>
                <Th className="text-right">Volume (USD)</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-slate-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-slate-400">
                    No users match these filters.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b hover:bg-slate-50 transition-colors"
                    data-testid={`admin-user-row-${u.id}`}
                  >
                    <Td>
                      <Link
                        to={`/admin/users/${u.id}`}
                        className="text-blue-600 hover:underline font-medium"
                        data-testid={`admin-user-link-${u.id}`}
                      >
                        {u.legal_name || <span className="italic text-slate-400">— unnamed —</span>}
                      </Link>
                      {u.is_admin && (
                        <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                          admin
                        </span>
                      )}
                    </Td>
                    <Td className="text-slate-600">{u.email}</Td>
                    <Td className="text-slate-600">{fmtBT(u.business_type)}</Td>
                    <Td>
                      <KybBadge status={u.kyb_status} />
                    </Td>
                    <Td className="text-slate-500 text-xs">
                      {u.onboarded_at ? new Date(u.onboarded_at).toLocaleDateString("en-IN") : "—"}
                    </Td>
                    <Td className="text-right sf-mono text-slate-700">
                      {u.total_volume_usd ? `$${u.total_volume_usd.toLocaleString("en-IN")}` : "—"}
                    </Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function SelectFilter({ value, onChange, options, label, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="h-10 px-3 rounded-md border border-slate-300 text-sm bg-white"
    >
      {options.map((o, i) => (
        <option key={i} value={o}>
          {o === "" ? label : o.replaceAll("_", " ")}
        </option>
      ))}
    </select>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left font-semibold px-4 py-3 ${className}`}>{children}</th>;
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}

function KybBadge({ status }) {
  const map = {
    activated: "bg-emerald-50 text-emerald-700 border-emerald-200",
    submitted: "bg-blue-50 text-blue-700 border-blue-200",
    additional_info_requested: "bg-amber-50 text-amber-700 border-amber-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    draft: "bg-slate-100 text-slate-600 border-slate-200",
    not_started: "bg-slate-100 text-slate-400 border-slate-200",
  };
  const key = status || "not_started";
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded border font-medium ${map[key] || map.draft}`}>
      {key.replaceAll("_", " ")}
    </span>
  );
}

function fmtBT(v) {
  if (!v) return "—";
  return v.replaceAll("_", " ");
}
